import { useMemo } from 'react';
import { SummaryStatistics, SummaryStatisticsSkeleton } from '../../patients/views/details/SummaryStatistics';
import { useDashboardInsights } from './hooks/useDashboardInsights';
import { usePatientActivitySummaryStats } from './hooks/usePatientActivitySummaryStats';
import { NoActivityDataIndicator } from './NoActivityDataIndicator';
import { PatientsCharts } from './PatientsCharts';
import { PatientsChartsSkeleton } from './PatientsChartsSkeleton';

export interface OrgDashboardContentProps {
  activityTypeIds: string[];
  dateRange: [Date, Date] | null;
}

export function OrgDashboardContent({ activityTypeIds, dateRange }: OrgDashboardContentProps) {
  const {
    data: summaryStats,
    isLoading: isSummaryStatsLoading,
    isFetching: isSummaryStatsFetching
  } = usePatientActivitySummaryStats({ dateRange, activityTypeIds });

  const {
    activityTypeData,
    activityTimeSeriesData,
    activityTypeTotalRepsTimeSeriesData,
    granularity,
    isLoading: isActivityTypeSummaryLoading
  } = useDashboardInsights({
    dateRange,
    activityTypeIds
  });

  const isLoading = isSummaryStatsLoading || isActivityTypeSummaryLoading || !summaryStats || !activityTypeData;

  const hasActivityData = useMemo(() => {
    return summaryStats && Object.values(summaryStats).some((stat) => stat?.value !== 0);
  }, [summaryStats]);

  return (
    <div className="flex h-full flex-col">
      {isLoading && (
        <div>
          <SummaryStatisticsSkeleton />
          <PatientsChartsSkeleton />
        </div>
      )}

      {!isLoading && hasActivityData && (
        <>
          <SummaryStatistics summaryStats={summaryStats} isLoading={isSummaryStatsFetching} />
          <PatientsCharts
            activityTypeData={activityTypeData}
            activityTimeSeriesData={activityTimeSeriesData}
            activityTypeTotalRepsTimeSeriesData={activityTypeTotalRepsTimeSeriesData}
            granularity={granularity}
          />
        </>
      )}

      {!isLoading && !hasActivityData && <NoActivityDataIndicator className="mt-32" />}
    </div>
  );
}
